/**
 * Builds a compact financial summary for the LLM to reason over.
 *
 * Why a summary and not the raw rows: the transaction history runs to tens of
 * thousands of records. Sending them is impossible within a prompt, and even a
 * few hundred would crowd out the model's reasoning. So the interesting
 * structure is extracted up front — category totals, recurring charges, top
 * merchants — and only that is sent.
 *
 * The transactions handed in are the DASHBOARD-FILTERED set, which means:
 *   - categories excluded from the charts (Credit Card Repayments, the
 *     Transfer - * categories) are already gone
 *   - mortgage and shopping categories have been aggregated for display
 * That is the right basis for "where does my money go" questions. It is
 * flagged in the prompt so the model doesn't claim to see the full picture.
 */

import { normalise } from '@/lib/categoryMatcher';

const money = (n) => `$${(n || 0).toFixed(2)}`;

/** Detect charges that repeat at a regular cadence — subscriptions and bills. */
function findRecurring(debits) {
  const groups = new Map();

  for (const t of debits) {
    const key = normalise(t.description);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  }

  const recurring = [];
  for (const [key, rows] of groups) {
    if (rows.length < 3) continue;

    // Must span at least three distinct months, or it's a burst rather than a
    // subscription (e.g. three coffees in one week).
    const months = new Set(rows.map(r => `${r.year}-${r.month}`));
    if (months.size < 3) continue;

    const amounts = rows.map(r => r.amount || 0);
    const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
    if (avg <= 0) continue;

    // Consistent amounts are what separate a subscription from variable
    // spending at the same merchant.
    const spread = Math.sqrt(
      amounts.reduce((s, a) => s + (a - avg) ** 2, 0) / amounts.length
    );
    if (spread / avg > 0.25) continue;

    recurring.push({
      label: rows[0].description.slice(0, 60),
      avg,
      count: rows.length,
      months: months.size,
      monthlyCost: avg * (rows.length / months.size),
    });
  }

  return recurring.sort((a, b) => b.monthlyCost - a.monthlyCost).slice(0, 15);
}

export function buildFinancialSummary(transactions, budgets = []) {
  const debits = transactions.filter(t => t.type === 'debit');
  const credits = transactions.filter(t => t.type === 'credit');

  const monthKeys = new Set(transactions.map(t => `${t.year}-${t.month}`));
  const monthCount = Math.max(monthKeys.size, 1);

  // --- spending by category ---
  const byCategory = {};
  debits.forEach(t => {
    const c = t.category || 'Uncategorised';
    byCategory[c] = (byCategory[c] || 0) + (t.amount || 0);
  });
  const categoryRows = Object.entries(byCategory).sort(([, a], [, b]) => b - a);
  const totalSpending = categoryRows.reduce((s, [, v]) => s + v, 0);
  const totalIncome = credits.reduce((s, t) => s + (t.amount || 0), 0);

  // --- budgets, normalised to monthly ---
  const budgetByCategory = {};
  budgets.forEach(b => {
    const monthly = b.period === 'annual' ? (b.amount || 0) / 12 : (b.amount || 0);
    budgetByCategory[b.category] = monthly;
  });

  const budgetLines = categoryRows
    .filter(([cat]) => budgetByCategory[cat] != null)
    .map(([cat, total]) => {
      const actual = total / monthCount;
      const budget = budgetByCategory[cat];
      const delta = actual - budget;
      return `${cat}: budget ${money(budget)}/mo, actual ${money(actual)}/mo (${delta >= 0 ? 'over' : 'under'} by ${money(Math.abs(delta))})`;
    });

  // --- largest individual purchases ---
  const largest = [...debits]
    .sort((a, b) => (b.amount || 0) - (a.amount || 0))
    .slice(0, 10)
    .map(t => `${t.date} · ${(t.description || '').slice(0, 50)} · ${money(t.amount)} · ${t.category || 'uncategorised'}`);

  const recurring = findRecurring(debits);

  const categoryLines = categoryRows.map(([cat, total]) =>
    `${cat}: ${money(total)} total, ${money(total / monthCount)}/mo avg, ${((total / totalSpending) * 100).toFixed(1)}% of spending`
  );

  const recurringLines = recurring.map(r =>
    `${r.label}: ~${money(r.avg)} each, ${r.count}x over ${r.months} months (~${money(r.monthlyCost)}/mo)`
  );

  return {
    monthCount,
    totalSpending,
    totalIncome,
    monthlySpending: totalSpending / monthCount,
    monthlyIncome: totalIncome / monthCount,
    text: [
      `PERIOD: ${monthCount} month(s) of dashboard-filtered data.`,
      `Transfers between own accounts and credit-card repayments are EXCLUDED, so this reflects household spending, not total cash movement.`,
      ``,
      `Total spending: ${money(totalSpending)} (${money(totalSpending / monthCount)}/month)`,
      `Total income:   ${money(totalIncome)} (${money(totalIncome / monthCount)}/month)`,
      `Net:            ${money(totalIncome - totalSpending)} (${money((totalIncome - totalSpending) / monthCount)}/month)`,
      ``,
      `SPENDING BY CATEGORY (highest first):`,
      categoryLines.join('\n') || 'No categorised spending.',
      ``,
      `LIKELY RECURRING CHARGES (consistent amount, 3+ months):`,
      recurringLines.join('\n') || 'None detected.',
      ``,
      `BUDGETS VS ACTUAL:`,
      budgetLines.join('\n') || 'No budgets set.',
      ``,
      `LARGEST SINGLE PURCHASES:`,
      largest.join('\n') || 'None.',
    ].join('\n'),
  };
}
